import { Allotment } from 'allotment'
import { Dayjs } from 'dayjs'
import { useCallback, useEffect, useState } from 'react'

import { depotsSelectors, fetchDepots, saveDepot, toggleDepotStatus } from '@/entities/depots'
import { useDropdown } from '@/entities/dropdowns/libs/useDropdown'
import { DropdownOption } from '@/entities/dropdowns/models/DropdownOption'
import { Icon, IconButton, ToggleButton } from '@/shared/components'
import { DynamicForm } from '@/shared/components/DynamicForm/DynamicForm'
import { CustomGridToolbar } from '@/shared/components/GridTable/CustomGridToolbar'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { timeDayjsToHhMm, timeHhMmSsToDayjs } from '@/shared/lib/timeHelper'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faAdd, faClose, faEdit, faToggleOn } from '@fortawesome/pro-regular-svg-icons'
import { Divider, Grid, styled } from '@mui/material'
import { GridColDef, GridInputRowSelectionModel, GridValidRowModel } from '@mui/x-data-grid-premium'

import { getDepotFormDefinition, initialDepotFormValues } from './libs/getDepotFormDefinition'
import {
  StyledAddButton, StyledButtonsContainer, StyledContainer, StyledDescription, StyledHeader, StyledLeftPane, StyledRightPane,
} from './StyledOutletPage'

export type DepotsPageProps = {
  //
}

export const DepotsPage: React.FC<DepotsPageProps> = () => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridInputRowSelectionModel>()
  const [showDetails, setShowDetails] = useState(false)
  const [formType, setFormType] = useState<'add' | 'edit' | 'view'>('add')
  const [loading, setLoading] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)
  const [formValues, setFormValues] = useState<Record<string, unknown>>({})
  const depots = useAppSelector(state => depotsSelectors.selectAll(state))

  const dispatch = useAppDispatch()
  const { setToastContent } = useToastContext()
  const activeGroupId = useAppSelector(state => state.userInterface.activeGroupId)
  const { data: colorOptions, completed: fetchColorsCompleted } = useDropdown('htmlColors')
  const { data: groupOptions, completed: fetchGroupsCompleted } = useDropdown('groups')

  // Fetch depots from server
  const fetchDepotsFromServer = async () => {
    setLoading(true)
    await dispatch(fetchDepots())
    setLoading(false)
  }

  useEffect(() => {
    fetchDepotsFromServer()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupId, dispatch])

  // Define the form fields and contents
  const formDefinition = getDepotFormDefinition({
    defaultValues: undefined,
    selectOptions: {
      groupId: groupOptions,
      uiColor: colorOptions,
    },
  })

  const handleSubmit = async (data: Record<string, unknown>) => {
    setSubmitting(true)

    const selectedDepot = depots.find(depot => depot.id === rowSelectionModel)

    try {
      if (formType === 'edit' && selectedDepot) {
        const editPayload = {
          ...selectedDepot,
          ...data,
          groupId: (data.groupId as DropdownOption).value,
          uiColor: (data.uiColor as DropdownOption).value,
          siteTime: timeDayjsToHhMm(data.siteTime as Dayjs),
          loadTime: timeDayjsToHhMm(data.loadTime as Dayjs),
          unloadTime: timeDayjsToHhMm(data.unloadTime as Dayjs),
          loadingBayGap: timeDayjsToHhMm(data.loadingBayGap as Dayjs),
          locationHours: data.locationHours,
          ...(data.address as Record<string, unknown>),
        }

        dispatch(saveDepot({ depot: editPayload }))
          .then(() => {
            setFormType('view')
          })
      } else {
        const addPayload = {
          ...data,
          groupId: (data.groupId as DropdownOption).value,
          uiColor: (data.uiColor as DropdownOption).value,
          siteTime: timeDayjsToHhMm(data.siteTime as Dayjs),
          loadTime: timeDayjsToHhMm(data.loadTime as Dayjs),
          unloadTime: timeDayjsToHhMm(data.unloadTime as Dayjs),
          loadingBayGap: timeDayjsToHhMm(data.loadingBayGap as Dayjs),
          locationHours: data.locationHours,
          isActive: true,
          ...(data.address as Record<string, unknown>),
        }

        dispatch(saveDepot({ depot: addPayload }))
          .then(() => {
            setFormType('view')
          })
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleStatus = async (id: string) => {
    try {
      await dispatch(toggleDepotStatus({ id }))
      setToastContent({
        open: true,
        severity: 'success',
        title: 'Success',
        content: 'Depot status updated successfully',
      })
    } catch (error) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Error',
        content: 'Failed to update depot status',
      })
    }
  }

  // Update form values when selectedId changes
  const resetForm = useCallback(() => {
    if (formType === 'add') {
      setFormValues(initialDepotFormValues)
    }
    else {
      const selectedDepot = depots.find(depot => depot.id === rowSelectionModel)

      if (!fetchColorsCompleted || !fetchGroupsCompleted || !selectedDepot) return

      setFormValues({
        ...selectedDepot,
        groupId: groupOptions.find(g => g.value === selectedDepot.groupId),
        uiColor: colorOptions.find(c => c.value === selectedDepot.uiColor),
        siteTime: timeHhMmSsToDayjs(selectedDepot.siteTime),
        loadTime: timeHhMmSsToDayjs(selectedDepot.loadTime),
        unloadTime: timeHhMmSsToDayjs(selectedDepot.unloadTime),
        loadingBayGap: timeHhMmSsToDayjs(selectedDepot.loadingBayGap),
        address: {
          street: selectedDepot.street,
          suburb: selectedDepot.suburb,
          postcode: selectedDepot.postcode,
          state: selectedDepot.state,
          country: selectedDepot.country,
          x: selectedDepot.x,
          y: selectedDepot.y,
        },
        locationHours: selectedDepot.locationHours,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelectionModel, depots, formType, fetchColorsCompleted, fetchGroupsCompleted])

  useEffect(() => {
    if (!rowSelectionModel || !['edit', 'view'].includes(formType)) return

    resetForm()
  }, [rowSelectionModel, depots, formType, resetForm])

  // Define the columns for the grid
  const rows = depots.map(item => ({
    ...item,

    isSelectable: true,
  }))

  const columns: GridColDef[] = [
    {
      field: 'externalId',
      headerName: 'Code',
      width: 150,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
    },
    {
      field: 'groupId',
      headerName: 'Group',
      width: 150,
      valueGetter: (value) => {
        return groupOptions.find(g => g.value === value)?.label || ''
      },
    },
    {
      field: 'displayAddress',
      headerName: 'Address',
      width: 300,
      valueGetter: (_, row) => {
        return `${row.street}, ${row.suburb} ${row.state} ${row.postcode}`
      },
    },
    {
      field: 'uiColor',
      headerName: 'Color',
      width: 100,
      renderCell: (params) => (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: pxToRem(8) }}>
          <div
            style={{
              width: pxToRem(14),
              height: pxToRem(14),
              borderRadius: pxToRem(2),
              backgroundColor: params.value,
            }}
          />
          <span>{params.value}</span>
        </div>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      valueGetter: (value) => value ? 'Active' : 'Disabled',
    },
  ]

  const onViewRowDetail = (row: GridValidRowModel) => {
    setFormType('view')
    setShowDetails(true)
    setRowSelectionModel(row.id)
  }

  const onHandleAdd = () => {
    setFormType('add')
    setShowDetails(true)
    setRowSelectionModel(undefined)
    setFormValues(initialDepotFormValues)
  }

  const handleCloseDetail = () => {
    setRowSelectionModel(undefined)
    setShowDetails(false)
  }

  return (
    <StyledContainer>
      <Allotment separator={true} defaultSizes={[100, 100]}>
        <Allotment.Pane>
          <StyledLeftPane>
            <Grid container direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} wrap="nowrap">
              <Grid item>
                <StyledHeader variant="body1">Depots</StyledHeader>
                <StyledDescription>
                  Configure your distribution centers and warehouses. Setup operating hours and loading bay capacity.
                </StyledDescription>
              </Grid>
              <Grid item>
                <StyledAddButton
                  primary
                  startIcon={<Icon icon={faAdd} />}
                  onClick={onHandleAdd}
                  sx={{ minWidth: pxToRem(130) }}
                >
                  New Depot
                </StyledAddButton>
              </Grid>
            </Grid>
            <Divider sx={{ marginTop: pxToRem(16) }} />
            <StyledGridTable
              rows={rows}
              columns={columns}
              loading={loading}
              isDragDropEnabled={false}
              rowSelectionModel={rowSelectionModel}
              onRowClick={onViewRowDetail}
              initialState={{
                filter: {
                  filterModel: {
                    items: [{
                      field: 'isActive', operator: 'equals', value: 'Active',
                    }],
                  },
                },
              }}
              slots={{
                toolbar: CustomGridToolbar,
              }}
              sx={{
                '.MuiDataGrid-filler--pinnedRight': { display: 'none' },
              }}
            />
          </StyledLeftPane>
        </Allotment.Pane>
        <Allotment.Pane visible={showDetails} minSize={360}>
          <StyledRightPane $visible={showDetails}>
            <Grid container direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} wrap="nowrap">
              <Grid item>
                <StyledHeader variant="body1" sx={{ paddingLeft: 0 }}>
                  {formType === 'add' && 'Add a new depot'}
                  {formType === 'edit' && `Edit ${formValues?.name || ''} details`}
                  {formType === 'view' && `View ${formValues?.name || ''} details`}
                </StyledHeader>
              </Grid>
              <StyledButtonsContainer item>
                <ToggleButton
                  value="active"
                  primary
                  text
                  selected={false}
                  disabled={formType === 'add'}
                  onChange={() => handleToggleStatus(rowSelectionModel as string)}
                >
                  <Icon icon={faToggleOn} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                  Toggle Status
                </ToggleButton>
                <ToggleButton
                  value="checked"
                  primary
                  text
                  disabled={formType === 'add'}
                  selected={formType === 'edit'}
                  onChange={() => {
                    resetForm()
                    setFormType((prev) => prev === 'edit' ? 'view' : 'edit')
                  }}
                  sx={{ minWidth: pxToRem(100) }}
                >
                  <Icon icon={faEdit} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                  {formType === 'edit' ? 'Cancel Edit' : 'Edit Depot'}
                </ToggleButton>
                <IconButton size="small" onClick={() => handleCloseDetail()}>
                  <Icon icon={faClose} sx={{ fontSize: pxToRem(18) }} />
                </IconButton>
              </StyledButtonsContainer>
            </Grid>
            <DynamicForm
              {...formDefinition}
              initialValues={formValues}
              onSubmit={handleSubmit}
              isReadOnly={isSubmitting || !showDetails || formType === 'view'}
              onCancel={() => {
                resetForm()

                if (formType === 'add') {
                  setShowDetails(false)
                }

                setFormType('view')
              }}
              slotProps={{
                submitButton: {
                  disabled: isSubmitting,
                },
              }}
            />
          </StyledRightPane>
        </Allotment.Pane>
      </Allotment>
    </StyledContainer>
  )
}

const StyledGridTable = styled(GridTable)`
  height: calc(100% - ${pxToRem(86)});
`